import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Button,
  Switch,
  Select,
  Space,
} from "antd";
import moment from "dayjs";
import { Locale, ApiManager } from "../../../utils";
import TranslateBtn from "../../../components/product/TranslateBtn";
import CouponProductSelectModal from "./CouponProductSelectModal";
import CategorySelectModal from "./CategorySelectModal";
import { ScopeSelectionPreview } from "./ScopeSelectionTags";
import ImageUpload from "../../../components/media/ImageUpload";
import { JD_ID } from "../../../constants";
import { isBBShopMerchant } from "../../../utils/helpers/merchantHelpers";
import "./coupons.scss";

const i18n = Locale.getInstance();
const api = ApiManager.getInstance();
const { RangePicker } = DatePicker;

const emptySelection = () => ({ items: [], byId: {} });

const BENEFIT_TYPE_OPTIONS = [
  { value: "threshold_discount", label: "满减券" },
  { value: "single_product", label: "单品券" },
  { value: "free_shipping", label: "包邮券" },
  { value: "payment_reward", label: "支付有礼券" },
];

const THRESHOLD_SCOPE_OPTIONS = [
  { value: "order", label: "整单可用" },
  { value: "product", label: "指定商品" },
  { value: "category", label: "指定分类" },
];

const REWARD_CHECKOUT_OPTIONS = [
  { value: "threshold_discount", label: "满减券" },
  { value: "single_product", label: "单品券" },
  { value: "free_shipping", label: "包邮券" },
];

function resolveBenefitType(coupon) {
  if (coupon?.benefit_type) {
    return coupon.benefit_type;
  }
  const scopeType = coupon?.scope_type || "order";
  const scopeIds = coupon?.scope_ids || [];
  if (scopeType === "shipping") {
    return "free_shipping";
  }
  if (scopeType === "product" && scopeIds.length === 1) {
    return "single_product";
  }
  return "threshold_discount";
}

async function hydrateProducts(token, user, ids = []) {
  if (!token || !ids.length) {
    return emptySelection();
  }

  let customApi = api.product.products;
  if (user?.merchant?.id === JD_ID) {
    customApi = api.product.jdProducts;
  }

  const byId = {};
  const idSet = new Set(ids.map((id) => Number(id)));
  let page = 1;
  let lastPage = 1;

  while (page <= lastPage && Object.keys(byId).length < idSet.size) {
    const res = await customApi.run({
      token,
      query: {
        category_id: -1,
        page,
        keyword: "",
        search_type: "name",
        count: 50,
      },
    });

    if (!res.result) {
      break;
    }

    const list = res.data?.data || [];
    list.forEach((product) => {
      if (idSet.has(product.id)) {
        byId[product.id] = product;
      }
    });

    lastPage = res.data?.last_page || 1;
    page += 1;
  }

  return {
    items: ids,
    byId,
  };
}

async function hydrateCategories(token, user, ids = []) {
  if (!token || !ids.length) {
    return emptySelection();
  }

  const categoryApi = isBBShopMerchant(user)
    ? api.product.categories
    : api.product.merchantCategories;

  const res = await categoryApi.run({ token });
  if (!res.result) {
    return { items: ids, byId: {} };
  }

  let list = [];
  if (Array.isArray(res.data?.merchant_categories)) {
    list = res.data.merchant_categories;
  } else if (Array.isArray(res.data?.data)) {
    list = res.data.data;
  } else if (Array.isArray(res.data)) {
    list = res.data;
  }

  const byId = {};
  list.forEach((category) => {
    if (ids.includes(category.id)) {
      byId[category.id] = category;
    }
  });

  return { items: ids, byId };
}

function ScopeSelectionField({
  disabled,
  label,
  selection,
  onOpenModal,
  onRemove,
  onClear,
}) {
  return (
    <Form.Item label={label}>
      <Space direction="vertical" style={{ width: "100%" }}>
        <Button disabled={disabled} onClick={onOpenModal}>
          {i18n.t("sales.coupons.select")}
        </Button>
        <ScopeSelectionPreview
          selection={selection}
          disabled={disabled}
          onRemove={onRemove}
          onClear={onClear}
          onShowAll={onOpenModal}
        />
      </Space>
    </Form.Item>
  );
}

function EditCouponForm({ visible, onFinish, onCancel, coupon, user }) {
  const [form] = Form.useForm();
  const benefitType = Form.useWatch("benefit_type", form);
  const scopeType = Form.useWatch("scope_type", form);
  const rewardCheckoutType = Form.useWatch("reward_checkout_type", form);
  const [selectedProducts, setSelectedProducts] = useState(emptySelection());
  const [selectedCategories, setSelectedCategories] = useState(emptySelection());
  const [showProductModal, setShowProductModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  const isPaymentReward = benefitType === "payment_reward";
  const effectiveRewardType = isPaymentReward
    ? rewardCheckoutType || "threshold_discount"
    : benefitType;

  const usesProductScope =
    effectiveRewardType === "single_product" ||
    (effectiveRewardType === "threshold_discount" && scopeType === "product");
  const usesCategoryScope =
    effectiveRewardType === "threshold_discount" && scopeType === "category";
  const showFacePrice =
    effectiveRewardType === "threshold_discount" ||
    effectiveRewardType === "single_product";
  const showThreshold =
    effectiveRewardType === "threshold_discount" ||
    effectiveRewardType === "single_product" ||
    effectiveRewardType === "free_shipping";

  const activeSelection = usesProductScope
    ? selectedProducts
    : usesCategoryScope
      ? selectedCategories
      : emptySelection();

  const removeSelectionItem = (id) => {
    if (usesProductScope) {
      const next = {
        items: selectedProducts.items.filter((itemId) => itemId !== id),
        byId: { ...selectedProducts.byId },
      };
      delete next.byId[id];
      setSelectedProducts(next);
      return;
    }

    if (usesCategoryScope) {
      const next = {
        items: selectedCategories.items.filter((itemId) => itemId !== id),
        byId: { ...selectedCategories.byId },
      };
      delete next.byId[id];
      setSelectedCategories(next);
    }
  };

  const clearSelection = () => {
    const next = emptySelection();
    if (usesProductScope) {
      setSelectedProducts(next);
    } else if (usesCategoryScope) {
      setSelectedCategories(next);
    }
  };

  useEffect(() => {
    let cancelled = false;

    async function loadCouponState() {
      if (!coupon) {
        return;
      }

      if (coupon.id || coupon.duplicate) {
        const {
          ug_name,
          zh_name,
          face_price,
          start_price,
          quantity,
          expire_days,
          claim_limit_per_user,
          zh_usage_notice,
          ug_usage_notice,
          status,
          start_date,
          end_date,
          is_public_coupon,
          scope_type,
          scope_ids,
          scope_threshold_type,
          benefit_type,
          reward_checkout_type,
          reward_popup_image,
        } = coupon;

        const duplicateSuffix = coupon.duplicate
          ? i18n.t("sales.coupons.duplicateNameSuffix")
          : "";
        const nextBenefitType = resolveBenefitType(coupon);
        const nextRewardCheckoutType =
          reward_checkout_type ||
          coupon.usable_benefit_type ||
          "threshold_discount";
        const nextScopeType =
          nextBenefitType === "single_product" ||
          (nextBenefitType === "payment_reward" &&
            nextRewardCheckoutType === "single_product")
            ? "product"
            : scope_type === "shipping"
              ? "order"
              : scope_type || "order";
        const nextScopeIds = scope_ids || [];
        const popupImages = reward_popup_image
          ? [{ url: reward_popup_image, path: reward_popup_image }]
          : [];

        form.setFieldsValue({
          ug_name: coupon.duplicate ? `${ug_name || ""}${duplicateSuffix}` : ug_name,
          zh_name: coupon.duplicate ? `${zh_name || ""}${duplicateSuffix}` : zh_name,
          face_price: face_price ? face_price / 100 : 0,
          use_start_price: start_price ? start_price / 100 : 0,
          quantity,
          expire_days: expire_days || 30,
          claim_limit_per_user:
            claim_limit_per_user != null ? claim_limit_per_user : 1,
          zh_usage_notice: zh_usage_notice || "",
          ug_usage_notice: ug_usage_notice || "",
          status: coupon.duplicate ? true : status === 1,
          is_public_coupon:
            nextBenefitType === "payment_reward" ? false : is_public_coupon === 1,
          range: [moment(start_date), moment(end_date)],
          benefit_type: nextBenefitType,
          reward_checkout_type: nextRewardCheckoutType,
          scope_type: nextScopeType,
          scope_ids: nextScopeIds,
          reward_popup_image: popupImages,
        });

        if (
          nextBenefitType === "single_product" ||
          nextScopeType === "product" ||
          (nextBenefitType === "payment_reward" &&
            nextRewardCheckoutType === "single_product")
        ) {
          const hydrated = await hydrateProducts(
            user?.token,
            user,
            nextScopeIds
          );
          if (!cancelled) {
            setSelectedProducts(hydrated);
            setSelectedCategories(emptySelection());
          }
          return;
        }

        if (nextScopeType === "category") {
          const hydrated = await hydrateCategories(
            user?.token,
            user,
            nextScopeIds
          );
          if (!cancelled) {
            setSelectedCategories(hydrated);
            setSelectedProducts(emptySelection());
          }
          return;
        }
      } else {
        form.setFieldsValue({
          benefit_type: "threshold_discount",
          reward_checkout_type: "threshold_discount",
          scope_type: "order",
          scope_ids: [],
          expire_days: 30,
          claim_limit_per_user: 1,
          zh_usage_notice: "",
          ug_usage_notice: "",
          reward_popup_image: [],
        });
      }

      if (!cancelled) {
        setSelectedProducts(emptySelection());
        setSelectedCategories(emptySelection());
      }
    }

    loadCouponState();

    return () => {
      cancelled = true;
      form.resetFields();
      setSelectedProducts(emptySelection());
      setSelectedCategories(emptySelection());
    };
  }, [coupon, form, user]);

  const disabled = coupon && coupon.id !== null;

  const handleFinish = (values) => {
    const scopeIds = usesProductScope
      ? selectedProducts.items
      : usesCategoryScope
        ? selectedCategories.items
        : [];

    let scope_type = "order";
    if (values.benefit_type === "threshold_discount") {
      scope_type = values.scope_type || "order";
    } else if (
      values.benefit_type === "single_product" ||
      (values.benefit_type === "payment_reward" &&
        values.reward_checkout_type === "single_product")
    ) {
      scope_type = "product";
    } else if (
      values.benefit_type === "payment_reward" &&
      values.reward_checkout_type === "threshold_discount"
    ) {
      scope_type = values.scope_type || "order";
    }

    onFinish({
      ...values,
      scope_type,
      scope_ids: scopeIds,
      is_public_coupon: values.benefit_type === "payment_reward" ? false : values.is_public_coupon,
    });
  };

  const scopeSelectionLabel =
    effectiveRewardType === "single_product"
      ? "适用商品"
      : scopeType === "category"
        ? "适用分类"
        : "适用商品";

  return (
    <>
      <Modal
        forceRender
        open={visible}
        onCancel={onCancel}
        footer={null}
        title={
          coupon?.duplicate
            ? i18n.t("sales.coupons.duplicateTitle")
            : coupon?.id
              ? i18n.t("sales.coupons.title")
              : i18n.t("sales.coupons.add")
        }
        width={700}
      >
        <Form
          form={form}
          onFinish={handleFinish}
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 14 }}
          initialValues={{
            benefit_type: "threshold_discount",
            reward_checkout_type: "threshold_discount",
            scope_type: "order",
            scope_ids: [],
            expire_days: 30,
            claim_limit_per_user: 1,
            zh_usage_notice: "",
            ug_usage_notice: "",
            reward_popup_image: [],
          }}
        >
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => (
              <Form.Item
                label={i18n.t("sales.zhTitle")}
                name="zh_name"
                rules={[
                  { required: true, message: i18n.t("sales.zhTitleWarning") },
                ]}
              >
                <Input
                  disabled={disabled}
                  addonAfter={
                    <TranslateBtn
                      context={getFieldValue("zh_name")}
                      onFinish={(value) =>
                        setFieldsValue({
                          ug_name: value,
                        })
                      }
                      from="zh"
                      to="ug"
                    />
                  }
                />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => (
              <Form.Item
                label={i18n.t("sales.ugTitle")}
                name="ug_name"
                rules={[
                  { required: true, message: i18n.t("sales.ugTitleWarning") },
                ]}
              >
                <Input
                  disabled={disabled}
                  addonAfter={
                    <TranslateBtn
                      context={getFieldValue("ug_name")}
                      onFinish={(value) =>
                        setFieldsValue({
                          zh_name: value,
                        })
                      }
                      from="ug"
                      to="zh"
                    />
                  }
                />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item label="优惠券类型" name="benefit_type">
            <Select
              options={BENEFIT_TYPE_OPTIONS}
              disabled={disabled}
              onChange={(value) => {
                setSelectedProducts(emptySelection());
                setSelectedCategories(emptySelection());
                form.setFieldsValue({ scope_ids: [] });
                if (value === "single_product") {
                  form.setFieldsValue({ scope_type: "product", use_start_price: 0 });
                } else if (value === "free_shipping") {
                  form.setFieldsValue({ scope_type: "order" });
                } else if (value === "payment_reward") {
                  form.setFieldsValue({
                    scope_type: "order",
                    is_public_coupon: false,
                    reward_checkout_type: "threshold_discount",
                  });
                } else {
                  form.setFieldsValue({ scope_type: "order" });
                }
              }}
            />
          </Form.Item>
          {isPaymentReward && (
            <Form.Item
              label="奖励券类型"
              name="reward_checkout_type"
              rules={[{ required: true, message: "请选择奖励券类型" }]}
            >
              <Select
                options={REWARD_CHECKOUT_OPTIONS}
                disabled={disabled}
                onChange={(value) => {
                  setSelectedProducts(emptySelection());
                  setSelectedCategories(emptySelection());
                  form.setFieldsValue({ scope_ids: [], scope_type: "order" });
                  if (value === "single_product") {
                    form.setFieldsValue({ scope_type: "product", use_start_price: 0 });
                  }
                }}
              />
            </Form.Item>
          )}
          {showFacePrice && (
            <Form.Item
              rules={[
                { required: true, message: i18n.t("sales.coupons.priceWarning") },
              ]}
              label={i18n.t("sales.coupons.price")}
              name="face_price"
            >
              <InputNumber disabled={disabled} />
            </Form.Item>
          )}
          {showThreshold && (
            <Form.Item
              rules={[
                { required: true, message: i18n.t("sales.coupons.limitWarning") },
              ]}
              label={
                effectiveRewardType === "free_shipping"
                  ? "满额包邮(元)"
                  : i18n.t("sales.coupons.limit")
              }
              name="use_start_price"
            >
              <InputNumber disabled={disabled} min={0} />
            </Form.Item>
          )}
          {(benefitType === "threshold_discount" ||
            (isPaymentReward && effectiveRewardType === "threshold_discount")) && (
            <Form.Item label="适用范围" name="scope_type">
              <Select
                options={THRESHOLD_SCOPE_OPTIONS}
                disabled={disabled}
                onChange={() => {
                  setSelectedProducts(emptySelection());
                  setSelectedCategories(emptySelection());
                  form.setFieldsValue({ scope_ids: [] });
                }}
              />
            </Form.Item>
          )}
          {(usesProductScope || usesCategoryScope) && (
            <ScopeSelectionField
              disabled={disabled}
              label={scopeSelectionLabel}
              selection={activeSelection}
              onOpenModal={() => {
                if (usesProductScope) {
                  setShowProductModal(true);
                } else {
                  setShowCategoryModal(true);
                }
              }}
              onRemove={removeSelectionItem}
              onClear={clearSelection}
            />
          )}
          {!isPaymentReward && (
            <Form.Item
              rules={[
                {
                  required: true,
                  message: i18n.t("sales.coupons.quantityWarning"),
                },
              ]}
              label={i18n.t("sales.coupons.quantity")}
              name="quantity"
            >
              <InputNumber />
            </Form.Item>
          )}
          {!isPaymentReward && (
            <Form.Item
              rules={[
                { required: true, message: i18n.t("sales.coupons.rangeWarning") },
              ]}
              label={i18n.t("sales.coupons.range")}
              name="range"
            >
              <RangePicker showTime />
            </Form.Item>
          )}
          <Form.Item
            rules={[
              {
                required: true,
                message: i18n.t("sales.coupons.expireDaysWarning"),
              },
            ]}
            label={i18n.t("sales.coupons.expireDays")}
            name="expire_days"
            extra={
              isPaymentReward
                ? "用户领取后的有效天数（发放时间和数量由支付有礼活动控制）"
                : i18n.t("sales.coupons.expireDaysHint")
            }
          >
            <InputNumber min={1} precision={0} disabled={disabled} addonAfter={i18n.t("sales.coupons.expireDaysUnit")} />
          </Form.Item>
          {!isPaymentReward && (
            <Form.Item
              rules={[
                {
                  required: true,
                  message: i18n.t("sales.coupons.claimLimitWarning"),
                },
              ]}
              label={i18n.t("sales.coupons.claimLimit")}
              name="claim_limit_per_user"
              extra={i18n.t("sales.coupons.claimLimitHint")}
            >
              <InputNumber min={0} precision={0} disabled={disabled} />
            </Form.Item>
          )}
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => (
              <Form.Item
                label={i18n.t("sales.coupons.usageNoticeZh")}
                name="zh_usage_notice"
                extra={
                  <TranslateBtn
                    context={getFieldValue("zh_usage_notice")}
                    onFinish={(value) =>
                      setFieldsValue({ ug_usage_notice: value })
                    }
                    from="zh"
                    to="ug"
                  />
                }
              >
                <Input.TextArea rows={4} disabled={disabled} />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item
            label={i18n.t("sales.coupons.usageNoticeUg")}
            name="ug_usage_notice"
          >
            <Input.TextArea rows={4} disabled={disabled} />
          </Form.Item>
          {isPaymentReward && (
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => (
                <Form.Item
                  label="支付成功弹窗图片"
                  name="reward_popup_image"
                  rules={[{ required: true, message: "请上传弹窗图片" }]}
                >
                  <ImageUpload
                    images={getFieldValue("reward_popup_image") || []}
                    setSelectedImages={(images) => {
                      setFieldsValue({ reward_popup_image: images || [] });
                    }}
                  />
                </Form.Item>
              )}
            </Form.Item>
          )}
          <Form.Item
            label={i18n.t("sales.coupons.isPublic")}
            name="is_public_coupon"
            valuePropName="checked"
            hidden={isPaymentReward}
          >
            <Switch disabled={isPaymentReward} />
          </Form.Item>
          <Form.Item
            label={i18n.t("sales.coupons.status")}
            name="status"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item wrapperCol={{ offset: 10 }}>
            <Button type="primary" htmlType="submit">
              {i18n.t("sales.submit")}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
      <CouponProductSelectModal
        visible={showProductModal}
        onCancel={() => setShowProductModal(false)}
        products={selectedProducts}
        disabled={disabled}
        maxSelection={
          benefitType === "single_product" ||
          (isPaymentReward && effectiveRewardType === "single_product")
            ? 1
            : 0
        }
        onSelect={(next) => {
          setSelectedProducts(next);
        }}
      />
      <CategorySelectModal
        visible={showCategoryModal}
        onCancel={() => setShowCategoryModal(false)}
        categories={selectedCategories}
        disabled={disabled}
        onSelect={(next) => {
          setSelectedCategories(next);
        }}
      />
    </>
  );
}

export default EditCouponForm;
